<template>
  <div class="day-limit-matrix">
    <div class="module-tabs mb-3">
      <b-nav tabs>
        <b-nav-item
          v-for="mod in modules"
          :key="mod.module"
          :active="activeModule === mod.module"
          @click="activeModule = mod.module"
        >
          {{ mod.moduleLabel }}
        </b-nav-item>
      </b-nav>
    </div>

    <div class="permission-table">
      <table class="permission-table-content" :class="{ 'is-dragging': !!dragSource }">
        <thead>
          <tr>
            <th class="group-header text-center">Nhóm</th>
            <th class="function-header">Chức năng</th>
            <th class="day-header text-center">Số ngày xem</th>
            <th class="day-header text-center">Số ngày chỉnh sửa</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in tableItems" :key="item.featureKey" class="function-row">
            <td
              v-if="item.isFirstInGroup"
              :rowspan="item.groupRowspan"
              class="group-cell align-middle"
            >
              {{ item.group }}
            </td>
            <td class="function-cell">{{ item.label }}</td>
            <td
              class="day-cell text-center"
              :class="{ 'drag-active': isDragTarget(item.featureKey, 'viewDays') }"
              @mousedown="onCellMousedown(item.featureKey, 'viewDays')"
              @mouseenter="onCellMouseenter(item.featureKey, 'viewDays')"
            >
              <b-input
                v-if="!readonly"
                :value="getDayValue(item.featureKey, 'viewDays')"
                type="number"
                min="1"
                size="sm"
                class="day-input text-center"
                :placeholder="getInputPlaceholder(item.featureKey, 'viewDays')"
                @input="onInput(item.featureKey, 'viewDays', $event)"
              />
              <span
                v-else
                class="day-value"
                :class="{ 'inherited-value': isDayValueInherited(item.featureKey, 'viewDays') }"
              >
                {{ formatDayValue(item.featureKey, 'viewDays') }}
                <small v-if="isDayValueInherited(item.featureKey, 'viewDays')" class="inherited-label">
                  (từ chức vụ)
                </small>
              </span>
            </td>
            <td
              class="day-cell text-center"
              :class="{ 'drag-active': isDragTarget(item.featureKey, 'editDays') }"
              @mousedown="onCellMousedown(item.featureKey, 'editDays')"
              @mouseenter="onCellMouseenter(item.featureKey, 'editDays')"
            >
              <b-input
                v-if="!readonly"
                :value="getDayValue(item.featureKey, 'editDays')"
                type="number"
                min="1"
                size="sm"
                class="day-input text-center"
                :placeholder="getInputPlaceholder(item.featureKey, 'editDays')"
                @input="onInput(item.featureKey, 'editDays', $event)"
              />
              <span
                v-else
                class="day-value"
                :class="{ 'inherited-value': isDayValueInherited(item.featureKey, 'editDays') }"
              >
                {{ formatDayValue(item.featureKey, 'editDays') }}
                <small v-if="isDayValueInherited(item.featureKey, 'editDays')" class="inherited-label">
                  (từ chức vụ)
                </small>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-if="!readonly" class="text-muted mt-1" style="font-size: 12px">
      Để trống = không giới hạn &nbsp;·&nbsp;
      <i class="fas fa-arrows-alt" style="font-size: 10px"></i>
      Giữ chuột và kéo để áp dụng cho nhiều ô
    </p>
  </div>
</template>

<script>
import { DAY_LIMIT_MATRIX } from '@/constants/dayLimitMatrix';

export default {
  name: 'DayLimitMatrix',
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    fallbackValue: {
      type: Object,
      default: () => ({}),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modules: DAY_LIMIT_MATRIX,
      activeModule: DAY_LIMIT_MATRIX[0].module,
      localValue: {},
      // drag state
      dragSource: null, // { featureKey, field, value }
      draggedCells: [],  // [{ featureKey, field }] — các ô đã được fill khi drag
    };
  },
  computed: {
    activeModuleData() {
      return this.modules.find(m => m.module === this.activeModule) || this.modules[0];
    },
    tableItems() {
      const items = [];
      for (const groupItem of this.activeModuleData.groups) {
        groupItem.features.forEach((feature, idx) => {
          items.push({
            featureKey: feature.key,
            label: feature.label,
            group: groupItem.group,
            isFirstInGroup: idx === 0,
            groupRowspan: groupItem.features.length,
          });
        });
      }
      return items;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.localValue = val ? { ...val } : {};
      },
    },
  },
  mounted() {
    document.addEventListener('mouseup', this.stopDrag);
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.stopDrag);
  },
  methods: {
    getFallback(featureKey, field) {
      const fb = this.fallbackValue && this.fallbackValue[featureKey];
      return fb && fb[field] != null ? fb[field] : null;
    },
    getDayValue(featureKey, field) {
      const entry = this.localValue[featureKey];
      return entry && entry[field] != null ? entry[field] : '';
    },
    getInputPlaceholder(featureKey, field) {
      const fb = this.getFallback(featureKey, field);
      return fb != null ? `${fb} (từ chức vụ)` : 'Không giới hạn';
    },
    formatDayValue(featureKey, field) {
      const entry = this.localValue[featureKey];
      if (entry && entry[field] != null) return entry[field] + ' ngày';
      const fb = this.getFallback(featureKey, field);
      return fb != null ? fb + ' ngày' : '—';
    },
    isDayValueInherited(featureKey, field) {
      const entry = this.localValue[featureKey];
      if (entry && entry[field] != null) return false;
      return this.getFallback(featureKey, field) != null;
    },
    isDragTarget(featureKey, field) {
      return this.draggedCells.some(c => c.featureKey === featureKey && c.field === field);
    },
    onCellMousedown(featureKey, field) {
      if (this.readonly) return;
      const current = this.localValue[featureKey] || {};
      const value = current[field] != null ? current[field] : null;
      this.dragSource = { featureKey, field, value };
      this.draggedCells = [];
    },
    onCellMouseenter(featureKey, field) {
      if (!this.dragSource) return;
      // bỏ qua ô nguồn
      if (featureKey === this.dragSource.featureKey && field === this.dragSource.field) return;
      this.applyDrag(featureKey, field);
    },
    applyDrag(featureKey, field) {
      const already = this.draggedCells.some(c => c.featureKey === featureKey && c.field === field);
      if (!already) {
        this.draggedCells = [...this.draggedCells, { featureKey, field }];
      }
      const current = this.localValue[featureKey] || { viewDays: null, editDays: null };
      this.localValue = {
        ...this.localValue,
        [featureKey]: { ...current, [field]: this.dragSource.value },
      };
      this.$emit('input', { ...this.localValue });
    },
    stopDrag() {
      this.dragSource = null;
      this.draggedCells = [];
    },
    onInput(featureKey, field, rawValue) {
      let parsed = rawValue === '' || rawValue === null ? null : parseInt(rawValue, 10);
      if (parsed !== null && parsed < 1) parsed = null;
      const current = this.localValue[featureKey] || { viewDays: null, editDays: null };
      this.localValue = {
        ...this.localValue,
        [featureKey]: { ...current, [field]: parsed },
      };
      this.$emit('input', { ...this.localValue });
    },
  },
};
</script>

<style scoped>
.permission-table {
  overflow-x: auto;
}

.permission-table-content {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.permission-table-content th,
.permission-table-content td {
  border: 1px solid #e4e6ef;
  padding: 8px 10px;
  vertical-align: middle;
}

.group-header {
  width: 20%;
  background-color: #f3f6f9;
  font-weight: 600;
  color: #3f4254;
}

.function-header {
  background-color: #f3f6f9;
  font-weight: 600;
  color: #3f4254;
}

.day-header {
  width: 18%;
  background-color: #f3f6f9;
  font-weight: 600;
  color: #3f4254;
}

.group-cell {
  background-color: #f8f9fc;
  font-weight: 600;
  color: #3f4254;
  text-align: center;
}

.function-cell {
  color: #3f4254;
}

.day-cell {
  padding: 4px 8px;
}

.function-row:hover {
  background-color: #fafafa;
}

.day-input {
  width: 120px;
  margin: 0 auto;
  display: block;
}

.day-value {
  font-weight: 500;
  color: #181c32;
}

.inherited-value {
  color: #7e8299;
  font-weight: 400;
}

.inherited-label {
  font-size: 10px;
  color: #a1a5b7;
}

/* Trạng thái đang drag */
.permission-table-content.is-dragging {
  user-select: none;
  cursor: cell;
}

/* Tắt pointer-events của input khi đang drag để mouseenter của td nhận được event */
.permission-table-content.is-dragging input {
  pointer-events: none;
}

/* Highlight ô đang được fill bởi drag */
.day-cell.drag-active {
  background-color: #e8f4fd;
  outline: 2px solid #3699ff;
  outline-offset: -2px;
}
</style>
